import type { Institution, GroupLicense, TestingLicense } from './license-definitions';
import type { Subscription } from './subscription-definitions';
import type { SectionRecord, SectionRecordWithJoins } from './section-definitions';
import type { CheckpointResults, SavedCustomCheckpoint } from './checkpoint-definitions';
import type { LessonRecord } from './lesson-record';
import type { JoinedSkillRecord, SkillRecord } from './skill-record';
import type { UPointsWithJoins } from './upoints-record';
import type { LocalLoginCredential, OAuthLoginCredential } from './login-credential-definitions';
import type { FlashRecord } from './flash-definitions';

export const userRoles = ['student', 'teacher', 'institution', 'crawler', 'superUser'] as const;
export type UserRole = typeof userRoles[number];

export const solfegeMethods = ['scaleDegrees', 'solfegeDoMinor', 'solfegeLaMinor'] as const;
export type SolfegeMethod = typeof solfegeMethods[number];

export const rhythmMethods = ['american', 'british'] as const;
export type RhythmMethod = typeof rhythmMethods[number];

export const uTheoryDisplayThemes = ['normal', 'high-contrast'] as const;
export type UTheoryDisplayTheme = typeof uTheoryDisplayThemes[number];

export interface UserPreferences {
  solfegeMethod?: SolfegeMethod;
  rhythmMethod?: RhythmMethod;
  /**
   * The display color theme
   */
  theme?: UTheoryDisplayTheme;
}

// export interface UserWithCredentials extends UserRecord {
//   credentials: (LocalLoginCredential | OAuthLoginCredential)[];
// }

export interface ClientUserWithCredentials extends Omit<UserRecord, 'password'> {
  credentials:
    | Pick<LocalLoginCredential, 'type' | 'username'>
    | Pick<OAuthLoginCredential, 'type' | 'oAuthProviderId' | 'oAuthUserId'>;
}

/**
 * A record on the 'users' table
 */
export interface UserRecord {
  id: string;
  /**
   * The section the user is currently viewing -- must be contained in either their owned
   * sections or their sectionIds list
   */
  sectionId: string;
  /**
   * The sections a user is enrolled in
   */
  sectionIds: string[];
  firstName: string | null;
  lastName: string | null;
  email: string | null;
  emailVerified: boolean | null;
  avatarUrl: string | null;
  /**
   * Accessibility accomodations for students
   */
  accommodations: Accommodation[];
  /**
   * Dictionary of testId: externalUserId
   */
  externalUniqueIds: {
    [testId: string]: string;
  };
  preferences?: UserPreferences;
  /** 'student' 'teacher' 'institution' */
  roles: UserRole[];
  schoolName?: string | null;
  teacherUrl?: string | null;
  teacherImageUrl?: string | null;
  teacherStatusVerified?: boolean | null;
  phone?: string | null;
  /** Teachers who teach for this institutional user */
  teacherIds?: string[];
  testIds?: string[];
  stripeCustomerId?: string | null;
  /* If referred by a campaign, we track that on registration */
  referrer?: string | null;
  createdAt: Date;
  updatedAt: Date;
  isAnonymous: boolean;
  demoUser?: boolean;
  lastLoginAt: Date;
  lastActiveAt: Date;
}

export interface SkillSectionDatum {
  numQuestions: number;
  percent: number;
  recentPercent: number;
  skillId: number;
  averageTime?: number;
  recentAverageTime?: number;
}

export interface UPointsSectionDatum {
  date: string | Date;
  points: number;
  totalTime: number;
  dailyPoints: number;
}

export type CheckpointSectionDatum = Pick<
  CheckpointResults,
  | 'id'
  | 'checkpointId'
  | 'checkpointName'
  | 'overall'
  | 'sections'
  | 'passing'
  | 'attemptStatus'
  | 'createdAt'
  | 'updatedAt'
>;

export interface SectionData {
  skills: SkillSectionDatum[];
  uPoints: UPointsSectionDatum[];
  checkpoints: CheckpointSectionDatum[];
  lessons: Omit<LessonRecord, 'progressData'>[];
}

/* As sent via websocket from /api/sections/{sectionId}/users */
export interface StudentUserRecord {
  email: string | null;
  firstName: string | null;
  id: string;
  lastName: string | null;
  avatarUrl?: string | null;
  checkpoints: CheckpointSectionDatum[];
  lessons: Pick<
    LessonRecord,
    'highestPageCompleted' | 'highestPageFirstCompletedOn' | 'lessonId'
  >[];
  skills: Pick<
    SkillRecord,
    'numQuestions' | 'percent' | 'recentAverageTime' | 'recentPercent' | 'skillId'
  >[];
  credentials: UserWithJoins['credentials'];
  uPoints: {
    /* Points recorded that day */
    dailyPoints: number;
    date: string | Date;
    /* Total current points for that day */
    points: number;
    /* Seconds */
    totalTime: number;
  }[];
  accommodations: Accommodation[];
  lastLoginAt: string | Date;
  lastActiveAt: string | Date;
}

/**
 * The record with potentially joined info from other tables
 */
export interface UserWithJoins extends Omit<UserRecord, 'sectionData'> {
  pagesRemaining: PagesRemaining;
  credentials: (
    | Pick<LocalLoginCredential, 'type' | 'username' | 'displayUsername'>
    | Pick<OAuthLoginCredential, 'type' | 'oAuthProviderId' | 'isClassroomConnected'>
  )[];
  tests: SavedCustomCheckpoint[];
  ownedTests: SavedCustomCheckpoint[];
  isSubscribed: boolean;
  isCanceling: boolean;
  hasEverSubscribed: boolean;
  isPaidByGroupLicense: boolean;
  flash: FlashRecord[];
  licenses: GroupLicense[];
  testingLicenses: TestingLicense[];
  teachesForInstitutions: Institution[];
  subscriptions: Subscription[];
  /**
   * Every section the user is enrolled in, owns, teaches, or TAs for.
   *
   * Filter by `ownerIds`/`teacherIds`/`teachingAssistantIds` to get the sections
   * a user teaches; see `AbstractUserStore.taughtSections`.
   */
  allSections: SectionRecordWithJoins[];
  checkpoints: Omit<CheckpointResults, 'questions'>[];
  lessons: Omit<LessonRecord, 'progressData'>[];
  skills: JoinedSkillRecord[];
  uPoints: UPointsWithJoins;
}

/**
 * Distinguishes a fully joined user record from the trimmed `StudentUserRecord` that
 * teacher-facing screens hand around.
 *
 * Keys on `allSections`, which is emitted by every endpoint that serves a
 * `UserWithJoins`. (It used to key on `currentSections`, since removed;
 * `Object.keys(...).includes(...)` is untyped, so that would have kept compiling and
 * started returning `false` for every real user the moment the field was dropped.)
 */
export function isUserWithJoins(user: unknown): user is UserWithJoins {
  return typeof user === 'object' && user !== null && 'allSections' in user;
}

/**
 * The sections a user is enrolled in as a student -- the replacement for the removed
 * `UserWithJoins.currentSections` field.
 *
 * The server derived `currentSections` from the *enrollment role*; there is no role on a
 * `SectionRecord`, so this reconstructs it from two properties of the payload:
 *
 * - `sectionIds` holds every section the user actually has an enrollment row in. The gate
 *   on it is load-bearing: for a teacher who is not enrolled in the default section,
 *   `loadJoinedUser` pushes that section onto `allSections` anyway, and
 *   `loadDefaultSectionForUser` maps it with no enrollments -- so all three id arrays are
 *   empty and it would otherwise look like an enrollment. `sectionIds` is computed before
 *   that push and so excludes it.
 * - `ownerIds`/`teacherIds`/`teachingAssistantIds` are populated only when the user holds
 *   a non-student enrollment in the section; `cleanSectionForStudent` empties all three
 *   otherwise. So "absent from all three" means "not owner, teacher or TA here".
 *
 * The one input this cannot reproduce is a user holding *both* a student and a teaching
 * enrollment in the same section. The server picks one row arbitrarily
 * (`loadSectionsForUser` uses `distinct: ['sectionId']` with no ordering) and is
 * therefore itself unstable there; this treats such a section as taught, not enrolled.
 */
export function enrolledSections<Section extends SectionRecord>(
  user: Pick<UserRecord, 'id' | 'sectionIds'> & { allSections: Section[] }
): Section[] {
  return user.allSections.filter(
    (section) =>
      user.sectionIds.includes(section.id) &&
      !section.ownerIds.includes(user.id) &&
      !section.teacherIds.includes(user.id) &&
      !section.teachingAssistantIds.includes(user.id)
  );
}

/**
 * The sections a user owns, teaches, or is a teaching assistant for -- the replacement for
 * the removed `UserWithJoins.sections` field, and the complement of `enrolledSections`.
 *
 * `ownerIds`/`teacherIds`/`teachingAssistantIds` are populated only when the *subject* of
 * the lookup holds a non-student enrollment in the section -- `loadSectionsForUser` passes
 * the subject's id to `mapPGSectionToSection`, not the requestor's, and
 * `cleanSectionForStudent` empties all three otherwise -- so this holds both when a user
 * loads their own record and when an admin looks somebody else up.
 *
 * Unlike `enrolledSections` it does not need to gate on `sectionIds`: the default section
 * that `loadJoinedUser` pushes onto `allSections` for teachers who are not enrolled in it
 * arrives with no enrollments, so the three id arrays exclude it already.
 */
export function taughtSections<Section extends SectionRecord>(
  user: Pick<UserRecord, 'id'> & { allSections: Section[] }
): Section[] {
  return user.allSections.filter(
    (section) =>
      section.ownerIds.includes(user.id) ||
      section.teacherIds.includes(user.id) ||
      section.teachingAssistantIds.includes(user.id)
  );
}

export interface PagesRemaining {
  date: Date | string;
  pages: number;
  anonymousPages: number;
}

export interface Accommodation {
  /**
   * Institution to which this accomodation applies -- if set, applies to all classes
   * within that institution. Either this or sectionId must be set for an accomodation
   * to be applied to a student
   */
  institutionId: string | null;

  /**
   * Section to which this accomodation applies -- if set, applies to just this
   * class. Either this or institutionId must be set for an accomodation
   * to be applied to a student
   */
  sectionId: string | null;

  /**
   * A custom test to which this accommodation should be applied -- useful in the
   * case of an auditioning student, for whom we only want to accommodate a specific
   * placement test within uTheory.
   */
  testId: string | null;

  /**
   * Coefficient by which to multiply time limits & expectations. Ex:
   * a 5-minute time limit for a test becomes 1-minutes when
   * accomodations.time is set to 2. Similarly, a skill that expects
   * completion in 30s now allows 60s.
   */
  time: number;

  /**
   * Coefficient by which to multiply accuracy & passing exectations for
   * tests and skills. A test that requires an 80% to pass would only
   * require a 60% to pass if accuracy is set to 0.75.
   */
  accuracy: number;
}
